import { useQuery } from "@apollo/client/react";
import { SelfAssessmentCompletionDocument } from "@/graphql/generated/operations";
import { ELEMENT_GROUPS, elementImageSrc, GROUP_ORDER } from "@/lib/elements";

// Power Quiz artwork on Grow · Play: a collage of the 30 power motifs, laid out
// as 6 columns (the 6 element groups) × 5 powers. A tile stays blurred until the
// power has a self-report value; since genesis merges the questionnaires into
// that value, answering the quiz uncovers tiles too.

// Column-major reading order: group 1's five powers fill the first column, etc.
// Pairs with `grid-flow-col` + `grid-rows-5` below so each column is one group.
const ELEMENTS_BY_COLUMN: readonly string[] = GROUP_ORDER.flatMap(
  (group) => ELEMENT_GROUPS[group].elements,
);

// A power counts as "rated" (sharp) once it has a self-report value, no matter
// which instrument produced it.
function isRated(result: number | null | undefined): boolean {
  return typeof result === "number";
}

export function PowerCollage({ className }: { className?: string }) {
  // cache-and-network because a quiz part can add a value to a power that had
  // none, and the artwork here is gated on exactly that. Cache-first would keep
  // a tile blurred until a full reload.
  const { data } = useQuery(SelfAssessmentCompletionDocument, {
    fetchPolicy: "cache-and-network",
  });

  // elements.ts widens elements to `string` to dodge the codegen's nominal
  // CreativityElement split, so cast the keys to string here (see lib/elements).
  const rated = new Set<string>(
    (data?.selfAssessmentResults ?? [])
      .filter((r) => isRated(r.result))
      .map((r) => r.element as string),
  );

  return (
    <div
      className={`relative overflow-hidden ${className ?? ""}`}
      data-graphql="SelfAssessmentCompletion"
      aria-hidden
    >
      <div className="grid h-full w-full grid-flow-col grid-cols-6 grid-rows-5">
        {ELEMENTS_BY_COLUMN.map((element) => {
          const src = elementImageSrc(element);
          const sharp = rated.has(element);
          return (
            <div key={element} className="relative overflow-hidden">
              {src ? (
                <img
                  src={src}
                  alt=""
                  className={`h-full w-full object-cover transition-all duration-700 ease-out ${
                    sharp ? "" : "scale-110 blur-[4px]"
                  }`}
                />
              ) : null}
              {sharp ? null : <div className="absolute inset-0 bg-page/40" />}
            </div>
          );
        })}
      </div>
    </div>
  );
}
